import React from 'react';
import { formatFieldValue } from '../utils/formatters';

// PDF Content Renderer Component - Pure rendering component for PDF export
const DDRPDFExporter = React.forwardRef(({ 
  well, 
  currentReport, 
  sectionData, 
  layoutConfig, 
  globalSettings, 
  processedHeaderHTML 
}, exportRef) => {

  // Generate the same global styles as DDR_v2.js
  const generateGlobalStyles = (layoutConfig) => {
    if (!layoutConfig?.globalStyles) return {};
    
    const styles = layoutConfig.globalStyles;
    return {
      fontFamily: styles.fontFamily || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      fontSize: styles.fontSize || '14px',
      color: styles.textColor || '#1e293b',
      backgroundColor: styles.backgroundColor || '#ffffff',
      '--section-spacing': styles.sectionSpacing || '20px',
      '--section-padding': styles.sectionPadding || '16px',
      '--group-spacing': styles.groupSpacing || '16px',
      '--group-padding': styles.groupPadding || '12px',
      '--border-color': styles.borderColor || '#e5e7eb',
      '--border-radius': styles.borderRadius || '8px',
      '--section-header-background': styles.sectionHeaderBackgroundColor || '#f8fafc',
      '--section-header-font-size': styles.sectionHeaderFontSize || '16px',
      '--section-header-font-weight': styles.sectionHeaderFontWeight || '600',
      '--section-header-text-color': styles.sectionHeaderTextColor || '#1e293b',
      '--report-container-padding': styles.reportContainerPadding || '20px',
      '--report-container-spacing': styles.reportContainerSpacing || '0px'
    };
  };

  // Generate the same custom CSS as DDR_v2.js
  const generateCustomCSS = (layoutConfig) => {
    if (!layoutConfig) return '';
    
    let css = '';
    
    // Add global styles as CSS variables and base styles
    if (layoutConfig.globalStyles) {
      css += `
        .ddr-pdf-container {
          --section-spacing: ${layoutConfig.globalStyles.sectionSpacing || '20px'};
          --section-padding: ${layoutConfig.globalStyles.sectionPadding || '16px'};
          --group-spacing: ${layoutConfig.globalStyles.groupSpacing || '16px'};
          --group-padding: ${layoutConfig.globalStyles.groupPadding || '12px'};
          --border-color: ${layoutConfig.globalStyles.borderColor || '#e5e7eb'};
          --border-radius: ${layoutConfig.globalStyles.borderRadius || '8px'};
          --section-header-background: ${layoutConfig.globalStyles.sectionHeaderBackgroundColor || '#f8fafc'};
          --section-header-font-size: ${layoutConfig.globalStyles.sectionHeaderFontSize || '16px'};
          --section-header-font-weight: ${layoutConfig.globalStyles.sectionHeaderFontWeight || '600'};
          --section-header-text-color: ${layoutConfig.globalStyles.sectionHeaderTextColor || '#1e293b'};
          --report-container-padding: ${layoutConfig.globalStyles.reportContainerPadding || '20px'};
          font-family: ${layoutConfig.globalStyles.fontFamily || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'};
          font-size: ${layoutConfig.globalStyles.fontSize || '14px'};
          color: ${layoutConfig.globalStyles.textColor || '#1e293b'};
          background-color: ${layoutConfig.globalStyles.backgroundColor || '#ffffff'};
          width: 100%;
          margin: 0;
          padding: 20px;
          box-sizing: border-box;
        }
        
        .pdf-section-container {
          border: 1px solid var(--border-color) !important;
          border-radius: var(--border-radius) !important;
          margin-bottom: var(--section-spacing) !important;
          background: ${layoutConfig.globalStyles.backgroundColor || '#ffffff'} !important;
          page-break-inside: avoid;
        }
        
        .pdf-section-header {
          background: var(--section-header-background) !important;
          font-size: var(--section-header-font-size) !important;
          font-weight: var(--section-header-font-weight) !important;
          color: var(--section-header-text-color) !important;
          padding: var(--section-padding) !important;
          border-bottom: 1px solid var(--border-color) !important;
          margin: 0;
        }
        
        .pdf-section-content {
          padding: var(--section-padding) !important;
        }
        
        .pdf-field-group {
          margin-bottom: var(--group-spacing) !important;
          padding: var(--group-padding) !important;
          border-radius: var(--border-radius) !important;
        }
        
        .pdf-table-container {
          width: 100%;
          overflow: visible;
        }
        
        .pdf-section-table {
          width: 100%;
          border-collapse: collapse;
          margin: 0;
        }
        
        .pdf-section-table th {
          background: var(--section-header-background) !important;
          color: var(--section-header-text-color) !important;
          font-weight: var(--section-header-font-weight) !important;
          padding: 8px;
          border: 1px solid var(--border-color);
          text-align: left;
        }
        
        .pdf-section-table td {
          padding: 8px;
          border: 1px solid var(--border-color);
          vertical-align: top;
        }
        
        .pdf-form-grid {
          display: grid;
          gap: var(--group-spacing);
        }
        
        .pdf-form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--group-spacing);
          align-items: start;
        }
        
        .pdf-form-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .pdf-form-field label {
          font-weight: 600;
          color: var(--section-header-text-color);
          font-size: 13px;
        }
        
        .pdf-form-field .field-value {
          padding: 8px;
          border: 1px solid var(--border-color);
          border-radius: 4px;
          background: white;
          min-height: 20px;
          word-wrap: break-word;
        }
        
        .pdf-header {
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid var(--border-color);
        }
        
        .pdf-no-data {
          text-align: center;
          color: #6b7280;
          font-style: italic;
          padding: 20px;
        }

        @media print {
          .ddr-pdf-container {
            margin: 0;
            padding: 10px;
          }
          .pdf-section-container {
            page-break-inside: avoid;
            margin-bottom: 15px !important;
          }
        }
      `;
    }
    
    // Add layout-specific custom CSS
    if (layoutConfig.globalStyles?.customCSS) {
      css += `\n/* Global Custom CSS */\n${layoutConfig.globalStyles.customCSS}\n`;
    }
    
    // Add section-specific CSS - exactly like DDR_v2.js
    if (layoutConfig.sections) {
      layoutConfig.sections.forEach(section => {
        if (section.enabled) {
          // Add table-specific custom CSS if available
          if (section.customTableCSS) {
            const tableClassName = `pdf-table-${section.id}`;
            css += `\n/* Custom CSS for ${section.name} table */\n`;
            css += section.customTableCSS
              .replace(/\btable\b/g, `.${tableClassName} table`)
              .replace(/\bth\b/g, `.${tableClassName} th`)
              .replace(/\btr\b/g, `.${tableClassName} tr`)
              .replace(/\btd\b/g, `.${tableClassName} td`)
              .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                // Add !important to critical properties that might be overridden
                const importantRules = rules.replace(
                  /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                  '$1$2 !important'
                );
                return `${selector}{${importantRules}}`;
              }) + '\n';
          }
          
          // Add group-specific CSS - exactly like DDR_v2.js
          if (section.groupSettings) {
            Object.entries(section.groupSettings).forEach(([groupId, settings]) => {
              if (settings.customCSS) {
                const groupClassName = `pdf-field-group-${section.id}-${groupId}`;
                css += `\n/* Custom CSS for ${section.name} - Group ${groupId} */\n`;
                css += settings.customCSS
                  .replace(/\.field-group/g, `.${groupClassName}`)
                  .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                    const importantRules = rules.replace(
                      /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                      '$1$2 !important'
                    );
                    return `${selector}{${importantRules}}`;
                  }) + '\n';
              }
            });
          }
        }
      });
    }
    
    return css;
  };

  // Memoized field value component (same as DDR_v2.js)
  const MemoizedFieldValue = React.memo(({ value, field, globalSettings }) => {
    if (value === null || value === undefined || value === '') {
      return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>-</span>;
    }

    const formattedValue = formatFieldValue(value, field, globalSettings);
    
    return (
      <span 
        style={{
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap'
        }}
        dangerouslySetInnerHTML={{
          __html: formattedValue
        }}
      />
    );
  });

  // Render table section (same logic as DDR_v2.js)
  const renderTableSection = (config, data) => {
    const tableData = Array.isArray(data) ? data : [];
    const tableClassName = `pdf-table-${config.id}`;
    
    return (
      <div className={`pdf-table-container ${tableClassName}`}>
        <table className="pdf-section-table">
          <thead>
            <tr>
              {config.fields
                .filter(field => field.enabled)
                .map(field => (
                  <th key={field.id} style={{ width: field.width !== 'auto' ? field.width : undefined }}>
                    {field.name}
                  </th>
                ))
              }
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {config.fields
                    .filter(field => field.enabled)
                    .map(field => (
                      <td key={field.id}>
                        <MemoizedFieldValue
                          value={row[field.dbColumn]}
                          field={field}
                          globalSettings={globalSettings}
                        />
                      </td>
                    ))
                  }
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={config.fields.filter(f => f.enabled).length}>
                  <div className="pdf-no-data">No data available</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Render form section - EXACTLY like DDR_v2.js
  const renderFormSection = (config, data) => {
    // For form sections, data comes as an array, so we need to get the first record
    const dataRecord = Array.isArray(data) && data.length > 0 ? data[0] : {};
    const formData = dataRecord;
    const enabledFields = config.fields.filter(field => field.enabled);
    
    // Group fields by their group property (like DDR_v2.js implementation)
    const fieldGroups = enabledFields.reduce((groups, field) => {
      const groupId = field.group || 1;
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(field);
      return groups;
    }, {});

    // Get group settings with fallback (like DDR_v2.js)
    const groupSettings = config.groupSettings || {
      1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
    };

    return (
      <div className="pdf-form-container">
        {Object.entries(fieldGroups)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .map(([groupId, fields]) => {
            const settings = groupSettings[groupId] || { itemsPerRow: 2, customCSS: '', name: `Group ${groupId}`, showGroupName: false };
            const groupClassName = `pdf-field-group-${config.id}-${groupId}`;

            return (
              <div key={groupId}
                className={`pdf-field-group ${groupClassName}`}
                style={{
                  gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                  ...(settings.customCSS && {
                    background: 'unset',
                    padding: 'unset',
                    border: 'unset',
                    borderRadius: 'unset',
                    margin: 'unset'
                  })
                }}>
                
                {/* Show group title based on showGroupName setting */}
                {settings.showGroupName && (
                  <div className="pdf-field-group-title">{settings.name}</div>
                )}

                {/* Check if this is an Excel-style template that needs table structure */}
                {settings.customCSS && settings.customCSS.includes('.field-grid') ? (
                  // Excel table structure with support for multiple columns
                  <div className="field-grid"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                      gap: '0'
                    }}>
                    {fields.map(field => (
                      field.width === 'full' ? (
                        // Full width field spans all columns
                        <div key={field.id}
                          className="field-row"
                          style={{
                            gridColumn: '1 / -1',
                            display: 'grid',
                            gridTemplateColumns: '1fr 2fr'
                          }}>
                          <div className="label">{field.name}</div>
                          <div className="value">
                            <MemoizedFieldValue
                              value={formData[field.dbColumn]}
                              field={field}
                              globalSettings={globalSettings}
                            />
                          </div>
                        </div>
                      ) : (
                        // Regular field - each field is a grid item that contains label+value
                        <div key={field.id} className="field-row">
                          <div className="label">{field.name}</div>
                          <div className="value">
                            <MemoizedFieldValue
                              value={formData[field.dbColumn]}
                              field={field}
                              globalSettings={globalSettings}
                            />
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                ) : (
                  // Standard grid structure
                  <div className="pdf-field-group-content"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                      gap: '16px'
                    }}>
                    {fields.map(field => (
                      <div key={field.id}
                        className={`pdf-label-value-item ${field.width === 'full' ? 'pdf-field-width-full' : ''}`}
                        style={field.width === 'full' ? { gridColumn: '1 / -1' } : {}}>
                        <span className="label">{field.name}</span>
                        <span className="value">
                          <MemoizedFieldValue
                            value={formData[field.dbColumn]}
                            field={field}
                            globalSettings={globalSettings}
                          />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        }
      </div>
    );
  };

  // Render section (same logic as DDR_v2.js)
  const renderSection = (section) => {
    if (!section.enabled) return null;

    const data = sectionData[section.id] || {};
    const sectionClassName = `pdf-section-${section.id}`;

    return (
      <div key={section.id} className={`pdf-section-container ${sectionClassName}`}>
        <div className="pdf-section-header">
          <h3>{section.name}</h3>
        </div>
        <div className="pdf-section-content">
          {section.presentationType === 'table' ? 
            renderTableSection(section, data) : 
            renderFormSection(section, data)
          }
        </div>
      </div>
    );
  };

  // Render the PDF content
  const globalStyles = generateGlobalStyles(layoutConfig);
  const customCSS = generateCustomCSS(layoutConfig);

  return (
    <div style={{ position: 'absolute', left: '-9999px', top: '0' }}>
      <style dangerouslySetInnerHTML={{ __html: customCSS }} />
      <div 
        ref={exportRef}
        className="ddr-pdf-container"
        style={globalStyles}
      >
        {/* Header */}
        {processedHeaderHTML && (
          <div 
            className="pdf-header"
            dangerouslySetInnerHTML={{ __html: processedHeaderHTML }}
          />
        )}

        {/* Sections */}
        {layoutConfig?.sections?.map(section => renderSection(section))}

        {/* Footer */}
        <div style={{ 
          marginTop: '30px', 
          textAlign: 'center', 
          fontSize: '12px', 
          color: '#6b7280',
          borderTop: '1px solid var(--border-color)',
          paddingTop: '10px'
        }}>
          Generated on {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  );
});

DDRPDFExporter.displayName = 'DDRPDFExporter';

export default DDRPDFExporter; 